import { TRACKER_HEALTH_DEFAULTS } from '@chukua/common';

export type TruckMapStatus = 'moving' | 'stopped' | 'late' | 'silent';

export const TRUCK_MAP_STATUSES: TruckMapStatus[] = ['moving', 'stopped', 'late', 'silent'];

export const TRUCK_MAP_STATUS_LABELS: Record<TruckMapStatus, string> = {
  moving: 'Moving',
  stopped: 'Stopped',
  late: 'Late',
  silent: 'Silent',
};

/**
 * The API's tracker-health numbers, shared through `TRACKER_HEALTH_DEFAULTS`: late after 3 missed reports and silent after 10. A
 * pushing tracker is expected every minute; a polled one (a vendor with no live feed) every polling interval, which the snapshot
 * carries per truck as `reportIntervalSeconds`, so a truck polled every 5 minutes is not called late after 3.
 */
const expectedReportMs = (reportIntervalSeconds: number | null) => (reportIntervalSeconds ?? TRACKER_HEALTH_DEFAULTS.pushOnlyExpectedIntervalSeconds) * 1000;
export const LATE_AFTER_MS = TRACKER_HEALTH_DEFAULTS.lateAfterIntervals * expectedReportMs(null);
export const SILENT_AFTER_MS = TRACKER_HEALTH_DEFAULTS.silentAfterIntervals * expectedReportMs(null);

/** Below walking pace, a truck is standing: GPS speed drifts a little even when parked. */
export const STOPPED_BELOW_MPS = 1;

interface StatusInput {
  speedMps: number | null;
  deviceTime: string;
}

/**
 * What the map shows for a truck, from its last fix and the clock. Derived on every tick rather than stored, so a truck that stops
 * reporting turns late and then silent without anything being sent about it. A fix with no speed is not claimed to be moving.
 */
export function truckMapStatus({ speedMps, deviceTime }: StatusInput, now: Date, reportIntervalSeconds: number | null = null): TruckMapStatus {
  const ageMs = now.getTime() - new Date(deviceTime).getTime();
  const intervalMs = expectedReportMs(reportIntervalSeconds);
  if (ageMs >= TRACKER_HEALTH_DEFAULTS.silentAfterIntervals * intervalMs) return 'silent';
  if (ageMs >= TRACKER_HEALTH_DEFAULTS.lateAfterIntervals * intervalMs) return 'late';
  return speedMps !== null && speedMps >= STOPPED_BELOW_MPS ? 'moving' : 'stopped';
}
